import type {
  ControllerRenderProps,
  Primitive,
  UseFormReturn,
} from "react-hook-form";
import type { ZodType } from "zod";
import type { FormFieldType } from "~/components/form/fields";
import type { BulkFormData } from "~/components/form/so-bulk-form";

const HEX_INPUT_OPTIONS_KEY: string = "HEX_INPUT_OPTIONS_KEY";

export type FormStructure<T> = {
  [K in keyof T]: {
    label: string;
    type: FormFieldType;
    required?: boolean;
    hidden?: boolean;
    options?: { value: any; label: string }[] | FormOption<any>[]; // This can be used to pass args
    placeholder?: string;
    default?: any;
    rules?: ZodType<T[K]>;
    className?: string;
    disabled?: boolean;

    // Modifier functions for special usecases
    modifyTypeFn?: (
      form: UseFormReturn<Record<keyof T, any>, any, Record<keyof T, any>>,
    ) => FormFieldType;
    disabledFn?: (
      form: UseFormReturn<Record<keyof T, any>, any, Record<keyof T, any>>,
      field: ControllerRenderProps<Record<keyof T, any>, any>,
    ) => boolean;
  };
} & {
  virtualFields?: {
    name: string;
    keys: (keyof T)[];
    label: string;
    type: FormFieldType;
  }[];
};

export type FormOption<T extends Primitive = Primitive> = {
  label: string;
  value: T;
  category: string;
};

export const hexInputOptions = (
  byteLength: number,
): { value: number; label: string }[] => {
  return [
    {
      label: HEX_INPUT_OPTIONS_KEY,
      value: byteLength,
    },
  ];
};

export const getHexInputOptions = (options: any[]): number => {
  const res = options.find((o) => o.label === HEX_INPUT_OPTIONS_KEY);
  if (!res) return 16;

  return res.value;
};

export function isBulkFormData<T>(d: any): d is BulkFormData<T> {
  return d && typeof d === "object" && "items" in d;
}
